import AssignmentIcon from '@mui/icons-material/Assignment';
import { Box, Button, Typography } from '@mui/material';
import React from 'react';
import { Link } from 'react-router-dom';
import { useLessonPlanEditor } from '../context/LessonPlanEditorContext';
import { LessonPlanRole, LessonPlanSessionModel } from '../object-actions/types/types';

/** Returns true if sessions 1 through throughSessionNumber (inclusive) are all completed. */
function areSessionsCompletedUpTo(
  sessions: LessonPlanSessionModel[],
  throughSessionNumber: number,
  getSessionWithEdits: (s: LessonPlanSessionModel) => LessonPlanSessionModel
): boolean {
  if (!sessions.length || throughSessionNumber < 1) return false;
  const sorted = [...sessions].sort((a, b) => (a.session_number ?? a.id) - (b.session_number ?? b.id));
  if (sorted.length < throughSessionNumber) return false;
  for (let i = 0; i < throughSessionNumber; i++) {
    const session = sorted[i];
    const merged = getSessionWithEdits(session);
    if (!merged.completed_at) return false;
  }
  return true;
}

export interface ProgressSurveyCtaProps {
  lessonPlanId: number;
  weekNum: 4 | 8 | 12;
  sessions: LessonPlanSessionModel[];
  /** Progress surveys are for coach, parent, or payer — not the athlete. */
  myRoles?: LessonPlanRole[];
}

const WEEK_LABELS: Record<4 | 8 | 12, string> = {
  4: 'Week 4',
  8: 'Week 8',
  12: 'Week 12',
};

const ProgressSurveyCta: React.FC<ProgressSurveyCtaProps> = ({ lessonPlanId, weekNum, sessions, myRoles }) => {
  const { getSessionWithEdits } = useLessonPlanEditor();

  const canTakeProgressSurvey =
    myRoles?.includes('coach') || myRoles?.includes('parent') || myRoles?.includes('payer');

  const isVisible =
    canTakeProgressSurvey && areSessionsCompletedUpTo(sessions, weekNum, getSessionWithEdits);

  if (!isVisible) {
    return null;
  }

  const href = `/lesson-plan/${lessonPlanId}/week/${weekNum}/progress-survey`;
  const label = weekNum === 12 ? 'Take the final Progress Survey' : `Take the Progress Survey`;

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 2,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        backgroundColor: (theme) =>
          theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.08)' : 'rgba(25, 118, 210, 0.04)',
      }}
      data-attr={`progress-survey-cta_week-${weekNum}`}
      data-testid={`progress-survey-cta--week-${weekNum}`}
    >
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {WEEK_LABELS[weekNum]} complete
      </Typography>
      <Button
        component={Link}
        to={href}
        variant="contained"
        color="primary"
        size="medium"
        startIcon={<AssignmentIcon />}
        data-attr="progress-survey-cta_button"
      >
        {label}
      </Button>
    </Box>
  );
};

export default ProgressSurveyCta;
